import React, { FC, RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Popup } from './Popup/Popup';
import { Button, ButtonSize, ButtonTheme } from '../common/Button';

import './ListManager.scss';
import { Icon } from '../common/Button/Icon';
import { Modifier } from '@popperjs/core';
import { DropdownContext } from '../../hooks/dropdownContext';
import { useTranslation } from 'react-i18next';

type Props = {
    readonly size?: ButtonSize;
    readonly theme?: ButtonTheme;
    readonly icon?: string;
    readonly label?: string;
    readonly options?: ReadonlyArray<{
        readonly id: string;
        readonly listName?: string;
        readonly eaudiobooksAmount?: number;
        readonly ebooksAmount?: number;
        readonly epressAmount?: number;
    }>;
    readonly onClick?: () => Promise<void> | void;
    readonly onSelect?: (listId: string) => Promise<void> | void;
    readonly onCreate?: (listName: string) => void;
    readonly disabled: boolean;
};

export const ListManager: FC<Props> = ({
    size = ButtonSize.SMALL,
    theme = ButtonTheme.NEUTRAL,
    icon = 'add',
    label,
    options,
    onClick,
    onSelect = () => {},
    onCreate,
    disabled
}) => {
    const { t } = useTranslation();
    const { setIsExpandedContext } = useContext(DropdownContext);
    const toggleRef = useRef<HTMLElement>(null);
    const scrollInnerRef = useRef<HTMLUListElement>(null);
    const newListInputRef = useRef<HTMLInputElement>(null);

    const [expanded, setExpanded] = useState(false);
    const [makingNewList, setMakingNewList] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [loading, setLoading] = useState(false);

    const handleClose = useCallback(() => {
        setExpanded(false);
        setMakingNewList(false);
        setNewListName('');
        setIsExpandedContext(false);
    }, [setIsExpandedContext]);

    const handleSelect = useCallback(
        (listId: string) => {
            const promise = onSelect(listId);
            handleClose();
            if (promise) {
                setLoading(true);
                promise.finally(() => setLoading(false));
            }
        },
        [onSelect, handleClose]
    );

    const handleCreateSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (newListName) {
                onCreate?.(newListName);
                setNewListName('');
                setIsExpandedContext(true);
            }
        },
        [newListName, onCreate, setIsExpandedContext]
    );

    useEffect(() => {
        if (makingNewList) {
            newListInputRef.current?.focus();
        }
    }, [makingNewList]);

    const sameWidthModifier = useMemo(
        (): Modifier<any, any>[] => [
            // https://github.com/floating-ui/floating-ui/issues/794#issuecomment-589253942
            {
                name: 'sameWidth',
                enabled: true,
                fn: ({ state }) => {
                    state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                phase: 'beforeWrite',
                requires: ['computeStyles']
            }
        ],
        []
    );

    const isLoading = loading || disabled;

    return (
        <div className="list-manager" data-isopen={expanded} data-makingnewlist={makingNewList}>
            {onClick ? (
                <div ref={toggleRef as RefObject<HTMLDivElement>} className="list-manager-button-group">
                    <Button
                        size={size}
                        theme={theme}
                        text={label}
                        title={label}
                        onClick={() => {
                            const promise = onClick();
                            if (promise) {
                                setLoading(true);
                                promise.finally(() => setLoading(false));
                            }
                        }}
                        icon={icon}
                        disabled={isLoading}
                        isLoading={loading}
                    />
                    <Button
                        size={size}
                        theme={theme}
                        title={label}
                        onClick={() => {
                            setExpanded(it => !it);
                            setIsExpandedContext(!expanded);
                        }}
                        icon={expanded ? 'expand_less' : 'expand_more'}
                        disabled={isLoading}
                    />
                </div>
            ) : (
                <Button
                    ref={toggleRef as RefObject<HTMLButtonElement>}
                    fullWidth={true}
                    size={size}
                    theme={theme}
                    text={label}
                    title={label}
                    onClick={() => {
                        setExpanded(it => !it);
                        setIsExpandedContext(!expanded);
                    }}
                    icon={icon}
                    iconRight={expanded ? 'expand_less' : 'expand_more'}
                    disabled={isLoading}
                    isLoading={loading}
                />
            )}
            <Popup
                target={toggleRef}
                visible={expanded}
                placement="bottom-start"
                gap={0}
                arrow={false}
                onClose={handleClose}
                portal={true}
                modifiers={sameWidthModifier}
            >
                <div className="list-manager-contents">
                    <ul ref={scrollInnerRef}>
                        {options?.map(({ id, listName, ebooksAmount, eaudiobooksAmount, epressAmount }) => (
                            <li key={id}>
                                <a onClick={() => handleSelect(id)} role="button">
                                    <Icon code={icon} />
                                    <span className="list-name">
                                        {listName ?? '(unnamed)'}
                                        <span className="count">
                                            ({(ebooksAmount ?? 0) + (eaudiobooksAmount ?? 0) + (epressAmount ?? 0)})
                                        </span>
                                    </span>
                                </a>
                            </li>
                        )) ?? null}
                    </ul>
                    {makingNewList ? (
                        <form className="new-list-input" onSubmit={handleCreateSubmit}>
                            <i className="material-icons">content_paste</i>
                            <input
                                ref={newListInputRef}
                                type="text"
                                placeholder={t('selectionList.enterName')}
                                value={newListName}
                                onChange={e => setNewListName(e.target.value)}
                            />
                            <button className="material-icons" type="submit">
                                done
                            </button>
                        </form>
                    ) : (
                        <Button
                            className="new-list"
                            fullWidth={true}
                            size={ButtonSize.SMALL}
                            theme={ButtonTheme.NEUTRAL}
                            text={t('selectionList.newSelection')}
                            onClick={() => {
                                setMakingNewList(true);
                                setIsExpandedContext(true);
                            }}
                            icon="content_paste"
                        />
                    )}
                </div>
            </Popup>
        </div>
    );
};
